import { useIsAuthenticated } from '@azure/msal-react';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router';

import TenantConfigManager from '../../singleton/tenant-config-manager';
import TenantSettingsManager, {
  DEFAULT_TENANT_REGIONAL_SETTINGS,
  TenantRegionalSettings,
} from '../../singleton/tenant-settings-manager';
import { TenantRegionalSettingsContext } from './context';

const manager = TenantSettingsManager.getInstance();

/**
 * `/config` is public and fetched once at boot, before this provider ever
 * mounts (`TenantConfigManager.setConfig` runs in main.tsx pre-render), so
 * regional settings are already resolved — no separate authenticated fetch,
 * no loading/error gate. Falls back to the UTC/ISO defaults only if this
 * somehow renders before boot resolves (e.g. tests without the boot sequence).
 */
const readConfigRegional = (): TenantRegionalSettings => {
  const regional = TenantConfigManager.getInstance().getRegionalConfig();
  return regional ? { ...regional } : { ...DEFAULT_TENANT_REGIONAL_SETTINGS };
};

export const TenantSettingsProvider = ({ children }: PropsWithChildren) => {
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const isTenantRoute = location.pathname.startsWith('/md/');
  const [settings, setSettings] = useState(manager.getSettings());

  const applySettings = useCallback((next: TenantRegionalSettings) => {
    manager.setSettings(next);
    setSettings(next);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !isTenantRoute) {
      manager.reset();
      setSettings(manager.getSettings());
      return;
    }

    applySettings(readConfigRegional());
  }, [isAuthenticated, isTenantRoute, applySettings]);

  const value = useMemo(
    () => ({ settings, applySettings }),
    [settings, applySettings]
  );

  return (
    <TenantRegionalSettingsContext.Provider value={value}>
      {children}
    </TenantRegionalSettingsContext.Provider>
  );
};
